import { toCustomerError } from '../utils/customerError';

import { API_BASE_URL } from '../config/apiEndpoints';

class MessageThreadsApiService {
  parseListResponse(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.threads)) return data.threads;
    if (Array.isArray(data?.messages)) return data.messages;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }

  async listThreads({ userId, ownerId, venueId, bookingId, status = 'open', limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (ownerId) params.set('owner_id', ownerId);
    if (venueId) params.set('venue_id', venueId);
    if (bookingId) params.set('booking_id', bookingId);
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));

    const response = await fetch(`${API_BASE_URL}/message-threads?${params.toString()}`);
    if (!response.ok) {
      throw new Error(toCustomerError('', { status: response.status, fallback: 'Could not load messages.' }));
    }
    const data = await response.json();
    return this.parseListResponse(data);
  }

  mergeThreadsById(...lists) {
    const byId = new Map();
    lists.flat().forEach((thread) => {
      if (!thread?.id) return;
      byId.set(thread.id, thread);
    });
    const merged = Array.from(byId.values());
    return this.dedupeThreadsByBookingId(merged);
  }

  /** One inbox row per booking — keep the thread with the most conversation messages. */
  dedupeThreadsByBookingId(threads) {
    const withoutBooking = [];
    const byBooking = new Map();
    threads.forEach((thread) => {
      const bookingId = thread?.booking_id || thread?.bookingId;
      if (!bookingId) {
        withoutBooking.push(thread);
        return;
      }
      const key = String(bookingId);
      const existing = byBooking.get(key);
      const count = Number(thread?.message_count) || 0;
      const existingCount = Number(existing?.message_count) || 0;
      if (!existing || count > existingCount) {
        byBooking.set(key, thread);
      } else if (count === existingCount) {
        const tTime = new Date(thread.updated_at || thread.created_at || 0).getTime();
        const eTime = new Date(existing.updated_at || existing.created_at || 0).getTime();
        if (tTime > eTime) {
          byBooking.set(key, thread);
        }
      }
    });
    return [...withoutBooking, ...Array.from(byBooking.values())].sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }

  filterConversationMessages(messages) {
    return Array.isArray(messages) ? messages : [];
  }

  filterThreadsForVenue(threads, venueId) {
    if (!venueId) return threads || [];
    const id = String(venueId);
    return (threads || []).filter(
      (thread) => String(thread?.venue_id || thread?.venueId || '') === id
    );
  }

  /** Venue dashboard: union threads by ownership and participation. */
  async listVenueDashboardThreads({ userId, ownerId, venueId, limit = 50, isAdmin = false } = {}) {
    // Admin viewing any venue: list all threads for that venue (API skips participant filter for admin role).
    if (isAdmin && venueId) {
      if (userId) {
        const adminThreads = await this.listThreads({ userId, venueId, limit });
        if (adminThreads.length > 0) return adminThreads;
      }
      return this.listThreads({ venueId, limit });
    }

    const requests = [];
    if (userId) {
      requests.push(this.listThreads({ userId, venueId, limit }));
    }
    if (ownerId) {
      requests.push(this.listThreads({ ownerId, venueId, limit }));
    }
    if (!requests.length && venueId) {
      requests.push(this.listThreads({ venueId, limit }));
    }
    if (!requests.length) return [];
    const results = await Promise.all(requests);
    return this.mergeThreadsById(...results);
  }

  threadAccessQuery({ userId, ownerId } = {}) {
    return {
      ...(userId ? { userId } : {}),
      ...(ownerId ? { ownerId } : {}),
    };
  }

  async getThreadMessagesForDashboard(threadId, { userId, ownerId, isAdmin = false, limit = 100, messageKind = 'conversation' } = {}) {
    if (!threadId) return [];
    const access = this.threadAccessQuery({ userId, ownerId });
    try {
      return await this.getThreadMessages(threadId, { ...access, limit, messageKind });
    } catch (error) {
      if (isAdmin) {
        return this.getThreadMessages(threadId, { limit, messageKind });
      }
      throw error;
    }
  }

  async sendMessageForDashboard(threadId, body, { userId, ownerId, isAdmin = false, senderId, receiverId } = {}) {
    const access = this.threadAccessQuery({ userId, ownerId });
    try {
      return await this.sendMessage(threadId, body, {
        ...access,
        senderId,
        ...(receiverId ? { receiverId } : {}),
      });
    } catch (error) {
      if (isAdmin && senderId) {
        return this.sendMessage(threadId, body, {
          senderId,
          ...(receiverId ? { receiverId } : {}),
        });
      }
      throw error;
    }
  }

  async getThreadMessages(threadId, { userId, ownerId, limit = 100, messageKind = 'conversation' } = {}) {
    if (!threadId) return [];
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (ownerId) params.set('owner_id', ownerId);
    if (limit) params.set('limit', String(limit));
    if (messageKind) params.set('message_kind', messageKind);

    const response = await fetch(`${API_BASE_URL}/message-threads/${threadId}/messages?${params.toString()}`);
    if (!response.ok) {
      throw new Error(toCustomerError('', { status: response.status, fallback: 'Could not load messages.' }));
    }
    const data = await response.json();
    return this.filterConversationMessages(this.parseListResponse(data));
  }

  async createThread({
    venueId,
    topic,
    bookingId,
    createdByUserId,
    creatorRole,
    organizerName,
    organizerEmail,
    participants = [],
    initialMessage,
    initialMessageReceiverId,
  }) {
    // initialMessage (if used) is stored in thread_messages only — never booking_notes.
    // Booking enquiry / manual notes use POST /bookings/{id}/notes via the backend on create.
    const hasOrganizerIdentity = Boolean(
      createdByUserId || organizerEmail || organizerName
    );
    if (!venueId || !topic?.trim()) {
      throw new Error('Missing required fields to create message thread');
    }
    if (!hasOrganizerIdentity && !(bookingId && initialMessage?.trim())) {
      throw new Error('Missing organiser identity for message thread');
    }

    const payload = {
      venue_id: venueId,
      topic: topic.trim(),
      ...(createdByUserId ? { created_by_user_id: createdByUserId } : {}),
      ...(bookingId ? { booking_id: bookingId } : {}),
      ...(creatorRole ? { creator_role: creatorRole } : {}),
      ...(organizerName ? { organizer_name: organizerName } : {}),
      ...(organizerEmail ? { organizer_email: organizerEmail } : {}),
      ...(Array.isArray(participants) && participants.length > 0 ? { participants } : {}),
      ...(initialMessage?.trim() ? { initial_message: initialMessage.trim() } : {}),
      ...(initialMessageReceiverId ? { initial_message_receiver_id: initialMessageReceiverId } : {}),
      ...(initialMessage?.trim() ? { initial_message_kind: 'conversation' } : {}),
    };

    const response = await fetch(`${API_BASE_URL}/message-threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let detail = '';
      try {
        const errorData = await response.json();
        detail = errorData?.detail || errorData?.message || errorData?.error || '';
      } catch (e) {
        // ignore JSON parse errors
      }
      throw new Error(toCustomerError(detail, { status: response.status, fallback: 'Could not start this conversation.' }));
    }

    return response.json();
  }

  async markThreadRead(threadId, { userId, ownerId } = {}) {
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (ownerId) params.set('owner_id', ownerId);
    const response = await fetch(
      `${API_BASE_URL}/message-threads/${threadId}/read?${params.toString()}`,
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new Error(toCustomerError('', { status: response.status, fallback: 'Could not update this conversation.' }));
    }
    return response.json();
  }

  async sendMessage(threadId, body, { senderId, receiverId, userId, ownerId } = {}) {
    if (!threadId || !body?.trim() || !senderId) {
      throw new Error('Thread ID, message body and sender_id are required');
    }

    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (ownerId) params.set('owner_id', ownerId);

    const payload = {
      body: body.trim(),
      sender_id: senderId,
      ...(receiverId ? { receiver_id: receiverId } : {}),
    };

    const response = await fetch(`${API_BASE_URL}/message-threads/${threadId}/messages?${params.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(toCustomerError('', { status: response.status, fallback: 'Could not send your message.' }));
    }

    return response.json();
  }
}

const messageThreadsApiService = new MessageThreadsApiService();
export default messageThreadsApiService;
